<template>
  <div class="admin-logs-container">
    <!-- Filtros -->
    <div v-if="showFilters" class="logs-filters card mb-3">
      <div class="card-body">
        <div class="row g-3">
          <div class="col-12 col-md-3" v-if="!hideAdminFilter">
            <label class="form-label small text-muted">Admin</label>
            <input
              type="number"
              class="form-control form-control-sm"
              v-model.number="localFilters.admin_id"
              placeholder="ID do admin"
              @change="applyFilters"
            />
          </div>
          <div class="col-12 col-md-3">
            <label class="form-label small text-muted">Ação</label>
            <select
              class="form-select form-select-sm"
              v-model="localFilters.action"
              @change="applyFilters"
            >
              <option :value="undefined">Todas</option>
              <optgroup
                v-for="group in groupedActions"
                :key="group.label"
                :label="group.label"
              >
                <option
                  v-for="a in group.actions"
                  :key="a"
                  :value="a"
                >
                  {{ actionLabel(a) }}
                </option>
              </optgroup>
              <optgroup v-if="ungroupedActions.length" label="Outras">
                <option
                  v-for="a in ungroupedActions"
                  :key="a"
                  :value="a"
                >
                  {{ actionLabel(a) }}
                </option>
              </optgroup>
            </select>
          </div>
          <div class="col-12 col-md-3" v-if="!hideTargetFilter">
            <label class="form-label small text-muted">Tipo de alvo</label>
            <select
              class="form-select form-select-sm"
              v-model="localFilters.target_type"
              @change="applyFilters"
            >
              <option :value="undefined">Todos</option>
              <option v-for="t in availableTargetTypes" :key="t" :value="t">
                {{ targetTypeLabel(t) }}
              </option>
            </select>
          </div>
          <div class="col-12 col-md-3" v-if="!hideTargetFilter">
            <label class="form-label small text-muted">ID do alvo</label>
            <input
              type="number"
              class="form-control form-control-sm"
              v-model.number="localFilters.target_id"
              placeholder="ID"
              @change="applyFilters"
            />
          </div>
          <div class="col-12 col-md-3">
            <label class="form-label small text-muted">De</label>
            <input
              type="date"
              class="form-control form-control-sm"
              v-model="localFilters.from"
              @change="applyFilters"
            />
          </div>
          <div class="col-12 col-md-3">
            <label class="form-label small text-muted">Até</label>
            <input
              type="date"
              class="form-control form-control-sm"
              v-model="localFilters.to"
              @change="applyFilters"
            />
          </div>
          <div class="col-12 col-md-3 d-flex align-items-end">
            <button
              class="btn btn-sm btn-outline-secondary"
              @click="clearFilters"
            >
              <i class="fa-solid fa-eraser me-1"></i>Limpar filtros
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Carregando...</span>
      </div>
    </div>

    <!-- Empty -->
    <div v-else-if="!items.length" class="text-center py-5 text-muted">
      <i class="fa-solid fa-clipboard-list fa-3x mb-3 opacity-25"></i>
      <p class="mb-0">Nenhum log encontrado</p>
    </div>

    <!-- Feed -->
    <div v-else>
      <div class="log-list">
        <div
          v-for="log in items"
          :key="log.id"
          class="log-item"
          :class="{ 'log-item-expanded': expanded[log.id] }"
        >
          <div class="log-item-header" @click="toggleExpand(log.id)">
            <div class="log-item-badges">
              <span class="badge bg-primary">{{ actionLabel(log.action) }}</span>
              <span v-if="log.target_type" class="badge bg-secondary">
                {{ targetTypeLabel(log.target_type) }}
                <span v-if="log.target_id"> #{{ log.target_id }}</span>
              </span>
            </div>
            <div class="log-item-desc">
              {{ log.description }}
            </div>
            <div class="log-item-meta">
              <span v-if="log.admin" class="text-muted small">
                <i class="fa-solid fa-user-shield me-1"></i>{{ log.admin.name }}
              </span>
              <span class="text-muted small">
                <i class="fa-solid fa-clock me-1"></i>{{ formatDate(log.created_at) }}
              </span>
              <button
                v-if="hasMeta(log)"
                type="button"
                class="btn btn-sm btn-link p-0 ms-2"
                @click.stop="toggleExpand(log.id)"
              >
                <i
                  class="fa-solid"
                  :class="expanded[log.id] ? 'fa-chevron-up' : 'fa-chevron-down'"
                ></i>
                {{ expanded[log.id] ? "Ocultar" : "Detalhes" }}
              </button>
            </div>
          </div>
          <div v-if="expanded[log.id] && hasMeta(log)" class="log-item-body">
            <small class="text-muted">Payload extra:</small>
            <pre class="log-meta">{{ JSON.stringify(log.meta, null, 2) }}</pre>
          </div>
        </div>
      </div>

      <!-- Paginação -->
      <div class="d-flex justify-content-between align-items-center mt-3" v-if="lastPage > 1">
        <span class="text-muted small">
          Mostrando {{ items.length }} de {{ total }} registros
        </span>
        <div class="d-flex gap-2">
          <button
            class="btn btn-sm btn-outline-secondary"
            :disabled="currentPage <= 1 || loading"
            @click="changePage(currentPage - 1)"
          >
            <i class="fa-solid fa-chevron-left"></i>
          </button>
          <span class="align-self-center small">
            Página {{ currentPage }} de {{ lastPage }}
          </span>
          <button
            class="btn btn-sm btn-outline-secondary"
            :disabled="currentPage >= lastPage || loading"
            @click="changePage(currentPage + 1)"
          >
            <i class="fa-solid fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  ADMIN_ACTION_GROUPS,
  labelOfAction,
  labelOfTarget,
} from "@/utils/adminActionLabels";

export default {
  name: "AdminActionLogsList",
  props: {
    baseFilters: {
      type: Object,
      default: () => ({}),
    },
    showFilters: {
      type: Boolean,
      default: true,
    },
    hideAdminFilter: {
      type: Boolean,
      default: false,
    },
    hideTargetFilter: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      items: [],
      currentPage: 1,
      lastPage: 1,
      total: 0,
      expanded: {},
      localFilters: {
        admin_id: undefined,
        action: undefined,
        target_type: undefined,
        target_id: undefined,
        from: undefined,
        to: undefined,
      },
      availableActions: [],
      availableTargetTypes: [],
    };
  },
  mounted() {
    this.loadFilters();
    this.fetchLogs();
  },
  computed: {
    groupedActions() {
      const available = new Set(this.availableActions);
      return ADMIN_ACTION_GROUPS.map((group) => ({
        label: group.label,
        actions: group.actions.filter((a) => available.has(a)),
      })).filter((g) => g.actions.length > 0);
    },
    ungroupedActions() {
      const grouped = new Set(
        ADMIN_ACTION_GROUPS.flatMap((g) => g.actions)
      );
      return this.availableActions.filter((a) => !grouped.has(a));
    },
  },
  watch: {
    baseFilters: {
      handler() {
        this.currentPage = 1;
        this.fetchLogs();
      },
      deep: true,
    },
  },
  methods: {
    async loadFilters() {
      try {
        const res = await this.$adminActionLogService.listFilters();
        this.availableActions = res.data?.actions || [];
        this.availableTargetTypes = res.data?.target_types || [];
      } catch (e) {
        console.error("Erro ao carregar filtros de logs:", e);
      }
    },
    async fetchLogs() {
      this.loading = true;
      try {
        const params = {
          page: this.currentPage,
          ...this.baseFilters,
        };
        Object.keys(this.localFilters).forEach((k) => {
          if (
            this.localFilters[k] !== undefined &&
            this.localFilters[k] !== "" &&
            this.localFilters[k] !== null
          ) {
            params[k] = this.localFilters[k];
          }
        });
        const res = await this.$adminActionLogService.list(params);
        this.items = res.data || [];
        this.currentPage = res.current_page || 1;
        this.lastPage = res.last_page || 1;
        this.total = res.total || 0;
      } catch (e) {
        console.error("Erro ao carregar logs:", e);
        this.$eventBus.$emit("showToast", {
          title: "Erro",
          msg: e.message || "Erro ao carregar logs de admin.",
        });
      } finally {
        this.loading = false;
      }
    },
    applyFilters() {
      this.currentPage = 1;
      this.fetchLogs();
    },
    clearFilters() {
      Object.keys(this.localFilters).forEach((k) => {
        this.localFilters[k] = undefined;
      });
      this.currentPage = 1;
      this.fetchLogs();
    },
    changePage(page) {
      if (page < 1 || page > this.lastPage) return;
      this.currentPage = page;
      this.fetchLogs();
    },
    toggleExpand(id) {
      this.$set(this.expanded, id, !this.expanded[id]);
    },
    hasMeta(log) {
      return log.meta && Object.keys(log.meta).length > 0;
    },
    actionLabel(action) {
      return labelOfAction(action);
    },
    targetTypeLabel(type) {
      return labelOfTarget(type);
    },
    formatDate(date) {
      return this.$moment(date).format("DD/MM/YYYY HH:mm");
    },
  },
};
</script>

<style scoped>
.log-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.log-item {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background-color: #fff;
  transition: all 0.15s;
}

.log-item:hover {
  border-color: #dee2e6;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
}

.log-item-header {
  padding: 14px 18px;
  cursor: pointer;
}

.log-item-badges {
  display: flex;
  gap: 6px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}

.log-item-desc {
  font-size: 0.9rem;
  color: #212529;
  margin-bottom: 6px;
  line-height: 1.4;
}

.log-item-meta {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.log-item-body {
  border-top: 1px solid #e9ecef;
  padding: 12px 18px;
  background-color: #f8f9fa;
  border-radius: 0 0 8px 8px;
}

.log-meta {
  background-color: #fff;
  padding: 10px;
  border-radius: 4px;
  border: 1px solid #e9ecef;
  font-size: 0.8rem;
  margin: 6px 0 0 0;
  max-height: 250px;
  overflow: auto;
}
</style>
